import { useEffect, type RefObject } from 'react'

/**
 * Motion helpers (docs/UX-PLAYBOOK.md §8): motion is feedback, never decoration that keeps running. Everything here is
 * off with prefers-reduced-motion, and where `matchMedia` is missing (old browsers, jsdom in tests): the final state
 * shows at once.
 */
export const reducedMotion = () => typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
export const motionOn = () => typeof window.matchMedia === 'function' && !reducedMotion()

/** Once at startup: `html[data-motion]` lets CSS hide `.reveal` items until they scroll into view. */
export function initMotion() {
  if (motionOn() && typeof IntersectionObserver !== 'undefined') document.documentElement.setAttribute('data-motion', '')
}

/**
 * Scroll reveal for every `.reveal` element under `root`, including ones rendered later (data arriving, new pages).
 * Items entering together rise one after another, 40 ms apart, at most 8 steps.
 */
export function useReveal(root: RefObject<HTMLElement | null>) {
  useEffect(() => {
    const el = root.current
    if (!el || !document.documentElement.hasAttribute('data-motion')) return
    const show = (t: Element) => { t.classList.add('in'); io.unobserve(t) }
    const io = new IntersectionObserver(
      (entries) => {
        entries
          .filter((e) => e.isIntersecting)
          .forEach((e, i) => {
            const t = e.target as HTMLElement
            t.style.setProperty('--i', String(Math.min(i, 7)))
            show(t)
            // anything above it was jumped over (a programmatic scroll): it never crossed the viewport, show it too
            el.querySelectorAll('.reveal:not(.in)').forEach((n) => { if (n.compareDocumentPosition(t) & Node.DOCUMENT_POSITION_FOLLOWING) show(n) })
          })
      },
      { rootMargin: '0px 0px -6% 0px' },
    )
    let raf = 0
    const scan = () => {
      cancelAnimationFrame(raf)
      raf = requestAnimationFrame(() => el.querySelectorAll('.reveal:not(.in)').forEach((n) => io.observe(n)))
    }
    scan()
    const mo = new MutationObserver(scan)
    mo.observe(el, { childList: true, subtree: true })
    return () => { cancelAnimationFrame(raf); io.disconnect(); mo.disconnect() }
  }, [root])
}

/** Small burst of sparks from the middle of `el` (a habit checked, a niat done): 6–16 particles, 1.1 s. */
export function sparkBurst(el: Element | null | undefined, n = 10) {
  if (!el || !motionOn()) return
  const r = el.getBoundingClientRect()
  const host = document.createElement('div')
  host.className = 'burst'
  host.setAttribute('aria-hidden', 'true')
  host.style.left = `${r.left + r.width / 2}px`
  host.style.top = `${r.top + r.height / 2}px`
  const count = Math.max(6, Math.min(16, n))
  for (let i = 0; i < count; i++) {
    const a = (i / count) * Math.PI * 2
    const d = 20 + (i % 3) * 9
    const s = document.createElement('i')
    s.className = 'spark'
    s.style.setProperty('--dx', `${Math.round(Math.cos(a) * d)}px`)
    s.style.setProperty('--dy', `${Math.round(Math.sin(a) * d)}px`)
    s.style.animationDelay = `${(i % 4) * 30}ms`
    host.append(s)
  }
  document.body.append(host)
  setTimeout(() => host.remove(), 1300)
}
